import { useRef } from "react";
import { pdrsAPI } from "../../lib/api";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Paperclip, Eye, Download, X, Upload } from "lucide-react";

const isPreviewable = (type = "") =>
  type.startsWith("image/") || type === "application/pdf" || type.startsWith("text/");

const formatSize = (bytes) =>
  bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const openAttachment = async (pdrId, att, download) => {
  const win = !download && isPreviewable(att.content_type) ? window.open("", "_blank") : null;
  try {
    const res = await pdrsAPI.getAttachment(pdrId, att.id);
    const url = URL.createObjectURL(new Blob([res.data], { type: att.content_type }));
    if (win) {
      win.location.href = url;
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    if (win) win.close();
    toast.error("Failed to open file");
  }
};

const AttachmentRow = ({ pdrId, att, onRemove }) => (
  <div className="flex items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded-sm text-sm" data-testid={`pdr-attachment-${att.id}`}>
    <Paperclip className="w-4 h-4 text-slate-500 shrink-0" />
    <span className="flex-1 truncate" title={att.filename}>{att.filename}</span>
    <span className="text-xs text-slate-500 shrink-0">{formatSize(att.size)}</span>
    {isPreviewable(att.content_type) && (
      <Button type="button" variant="ghost" size="sm" onClick={() => openAttachment(pdrId, att, false)} data-testid={`pdr-attachment-view-${att.id}`}>
        <Eye className="w-4 h-4" />
      </Button>
    )}
    <Button type="button" variant="ghost" size="sm" onClick={() => openAttachment(pdrId, att, true)} data-testid={`pdr-attachment-download-${att.id}`}>
      <Download className="w-4 h-4" />
    </Button>
    {onRemove && (
      <Button type="button" variant="ghost" size="sm" className="text-red-600" onClick={() => onRemove(att)} data-testid={`pdr-attachment-remove-${att.id}`}>
        <X className="w-4 h-4" />
      </Button>
    )}
  </div>
);

export const PDRAttachmentList = ({ pdrId, attachments = [], onRemove }) =>
  attachments.length === 0 ? (
    <p className="text-sm text-slate-500" data-testid="pdr-attachments-empty">No supporting files</p>
  ) : (
    <div className="space-y-2" data-testid="pdr-attachments-list">
      {attachments.map((att) => (
        <AttachmentRow key={att.id} pdrId={pdrId} att={att} onRemove={onRemove} />
      ))}
    </div>
  );

export const PDRAttachmentsField = ({ pdrId, attachments, onRemoveExisting, pendingFiles, setPendingFiles }) => {
  const inputRef = useRef(null);
  const addFiles = (e) => {
    setPendingFiles([...pendingFiles, ...Array.from(e.target.files || [])]);
    e.target.value = "";
  };
  return (
    <div className="space-y-2" data-testid="pdr-attachments-field">
      <div className="flex items-center justify-between">
        <Label>Supporting Files (optional)</Label>
        <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={() => inputRef.current?.click()} data-testid="pdr-attachments-add">
          <Upload className="w-4 h-4 mr-2" /> Add Files
        </Button>
        <input ref={inputRef} type="file" multiple className="hidden" onChange={addFiles} data-testid="pdr-attachments-input" />
      </div>
      {pdrId && attachments?.length > 0 && (
        <PDRAttachmentList pdrId={pdrId} attachments={attachments} onRemove={onRemoveExisting} />
      )}
      {pendingFiles.map((f, i) => (
        <div key={`${f.name}-${i}`} className="flex items-center gap-2 p-2 border border-dashed border-blue-300 bg-blue-50 rounded-sm text-sm" data-testid={`pdr-pending-file-${i}`}>
          <Paperclip className="w-4 h-4 text-blue-500 shrink-0" />
          <span className="flex-1 truncate">{f.name}</span>
          <span className="text-xs text-slate-500">{formatSize(f.size)} · to upload</span>
          <Button type="button" variant="ghost" size="sm" onClick={() => setPendingFiles(pendingFiles.filter((_, j) => j !== i))} data-testid={`pdr-pending-remove-${i}`}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      ))}
    </div>
  );
};
